/* eslint-disable no-restricted-syntax */
/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-template */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
// CUSTOM HOOKS
import { useLastLiftMessage } from "../socketMessageState/BankMessagesContext";
// COMPONENTS
import IconDefinition from "./IconDefinition";
// INTERFACES
import { RootState } from "../../../app/store";
import { SocketMessageInterface } from "../../../interfaces/Monitoring.interface";
import { LiftInterface } from "../../../interfaces/Lift.interface";
import {
  BankInterface,
  BankFloorInterface,
} from "../../../interfaces/Bank.interface";
import {
  RuleDefinition,
  RuleMemory,
} from "../../../interfaces/IconDefinitions";
import {
  findWinningRule,
  conditionMatchesSocket,
  updateMemoryWithMessage,
  matchesExprOnMessage,
} from "../../../utils/ruleEngine";
import { bitmaskPrefix } from "../../../utils/stateMapHelpers";
// CONSTANTS
import { BLANK_SOCKET_MESSAGE } from "../../../constants/monitoring";
import { DEFAULT_ELEVATOR_RULES_OBJECT } from "../../../constants/iconDefinition";

function IconDefinitionElevatorCustom({
  floor,
  lift,
  bank,
  floorIndex,
  frontDoors,
  rearDoors,
  adapterRules,
  stalenessTimeout = null,
}: {
  floor: BankFloorInterface;
  lift: LiftInterface;
  bank: BankInterface;
  floorIndex: number;
  frontDoors: boolean;
  rearDoors: boolean;
  adapterRules: RuleDefinition[];
  stalenessTimeout?: number | null;
}) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const DEFAULT_ICON = "elWarning";

  // Use adapter rules only when at least one rule has conditions configured,
  // otherwise fall back to default rules.
  const rules: RuleDefinition[] = useMemo(() => {
    const hasConfiguredRules =
      adapterRules &&
      adapterRules.length > 0 &&
      adapterRules.some(
        (r) =>
          r.conditions &&
          r.conditions.length > 0 &&
          r.activeWhen?.kind === "group" &&
          r.activeWhen?.children?.length > 0
      );
    return hasConfiguredRules ? adapterRules : DEFAULT_ELEVATOR_RULES_OBJECT;
  }, [adapterRules]);

  // ************************************************ */
  // USE STATE VARIABLES **************************** */
  const [memory, setMemory] = useState<RuleMemory>({});
  const [icon, setIcon] = useState("elOutIndicator");
  const [activeRule, setActiveRule] = useState<RuleDefinition | null>(null);
  const [isStale, setIsStale] = useState(false);
  const lastIconChangeRef = useRef<number>(Date.now());
  const stalenessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const socketMessageLift: SocketMessageInterface = useLastLiftMessage(
    lift.id
  ) || { ...BLANK_SOCKET_MESSAGE };
  const hasFaults: boolean = socketMessageLift.faults;

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  const getDoorIconFromDisplayMap = (): string => {
    const dm = socketMessageLift.bitmask;
    const p = bitmaskPrefix(
      socketMessageLift.bank_id,
      socketMessageLift.lift_id
    );
    const frontOpen = dm[`${p}Usage:FrontDoorOpened`] ?? false;
    const rearOpen = dm[`${p}Usage:RearDoorOpened`] ?? false;
    const frontClosed = dm[`${p}Usage:FrontDoorClosed`] ?? false;
    const rearClosed = dm[`${p}Usage:RearDoorClosed`] ?? false;

    if (frontOpen && rearOpen) return "elFrontOpenRearOpen";
    if (frontOpen && rearClosed) return "elFrontOpenRearClose";
    if (frontClosed && rearOpen) return "elFrontCloseRearOpen";
    if (frontClosed && rearClosed) return "elFrontCloseRearClose";
    if (frontOpen) return "elOpen";
    if (frontClosed) return "elClose";
    return DEFAULT_ICON;
  };

  const activeFloors = useMemo(() => {
    if (!socketMessageLift) {
      return {
        car: new Set<number>(),
        hallUp: new Set<number>(),
        hallDown: new Set<number>(),
        locks: new Set<number>(),
      };
    }

    const car = new Set([
      ...socketMessageLift.car_calls.front,
      ...socketMessageLift.car_calls.rear,
    ]);

    const hallUp = new Set([
      ...socketMessageLift.hall_calls.up_assigned.front,
      ...socketMessageLift.hall_calls.up_assigned.rear,
    ]);

    const hallDown = new Set([
      ...socketMessageLift.hall_calls.down_assigned.front,
      ...socketMessageLift.hall_calls.down_assigned.rear,
    ]);

    const locks = new Set([
      ...socketMessageLift.car_calls.front_lock,
      ...socketMessageLift.car_calls.rear_lock,
      ...socketMessageLift.hall_calls.up_assigned.front_lock,
      ...socketMessageLift.hall_calls.up_assigned.rear_lock,
      ...socketMessageLift.hall_calls.down_assigned.front_lock,
      ...socketMessageLift.hall_calls.down_assigned.rear_lock,
    ]);

    return { car, hallUp, hallDown, locks };
  }, [socketMessageLift]);

  // Track icon changes and manage staleness timer
  useEffect(() => {
    lastIconChangeRef.current = Date.now();
    setIsStale(false);

    if (stalenessTimerRef.current) clearTimeout(stalenessTimerRef.current);
    if (stalenessTimeout !== null && stalenessTimeout > 0) {
      stalenessTimerRef.current = setTimeout(() => {
        setIsStale(true);
      }, stalenessTimeout);
    }
  }, [icon, stalenessTimeout]);

  // Cleanup staleness timer on unmount
  useEffect(
    () => () => {
      if (stalenessTimerRef.current) clearTimeout(stalenessTimerRef.current);
    },
    []
  );

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */

  // Reset accumulated memory when the rule set changes
  useEffect(() => {
    setMemory({});
    setActiveRule(null);
  }, [rules]);

  useEffect(() => {
    if (!socketMessageLift) return;

    if (activeRule) {
      // Check if the active rule's removeWhen fires (instant, scoped to its own conditions)
      const shouldRemove = matchesExprOnMessage(
        activeRule.removeWhen,
        activeRule.conditions,
        socketMessageLift,
        conditionMatchesSocket
      );

      if (shouldRemove) {
        setActiveRule(null);
        setIcon((prev) => (prev === DEFAULT_ICON ? prev : DEFAULT_ICON));
        setMemory({});
        return;
      }

      // Check if a higher-priority rule now matches (instant, scoped to each rule's conditions)
      const replacer = rules.find((r) => {
        if (r.id === activeRule.id) return false;
        return matchesExprOnMessage(
          r.activeWhen,
          r.conditions,
          socketMessageLift,
          conditionMatchesSocket
        );
      });

      if (replacer) {
        setActiveRule(replacer);
        setIcon((prev) => (prev === replacer.icon ? prev : replacer.icon));
        setMemory({});
        return;
      }

      setIcon((prev) => (prev === activeRule.icon ? prev : activeRule.icon));
      return;
    }

    // No active rule — accumulate memory and look for a winner
    const nextMemory = updateMemoryWithMessage(
      memory,
      rules,
      socketMessageLift,
      conditionMatchesSocket
    );
    const winner = findWinningRule(rules, nextMemory);

    if (winner) {
      setActiveRule(winner);
      setIcon(winner.icon);
      setMemory({});
    } else {
      setMemory(nextMemory);
      setIcon((prev) => (prev === DEFAULT_ICON ? prev : DEFAULT_ICON));
    }
  }, [socketMessageLift]);

  // Compute effective icon -- fall back to door state when direction icon is stale
  const effectiveIcon =
    isStale && (icon === "elUp" || icon === "elDown")
      ? getDoorIconFromDisplayMap()
      : icon;

  // -----------------------------
  // Render icon per floor
  const getIcon = () => {
    if (!socketMessageLift) return null;

    if (floor.floor_index === socketMessageLift.position) {
      if (socketMessageLift.position < 0) {
        return (
          <IconDefinition
            isMap={false}
            expanded={false}
            liftType="ELEVATOR"
            icon="elWarning"
            iconSize={20}
          />
        );
      }
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon={effectiveIcon}
          iconSize={20}
        />
      );
    }

    if (activeFloors.locks.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elLocked"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallDown.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalRed"
          iconSize={20}
        />
      );
    }

    if (activeFloors.hallUp.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoalGreen"
          iconSize={20}
        />
      );
    }

    if (activeFloors.car.has(floor.floor_index)) {
      return (
        <IconDefinition
          isMap={false}
          expanded={false}
          liftType="ELEVATOR"
          icon="elGoal"
          iconSize={20}
        />
      );
    }

    return null;
  };

  const containerClass =
    `monitoring__card__liftContainer__${theme} ` +
    (hasFaults ? "monitoringView__dangerElevator " : "") +
    (floorIndex === 0
      ? hasFaults
        ? "monitoringView__dangerElevator__top "
        : ""
      : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? hasFaults
        ? "monitoringView__dangerElevator__bottom "
        : ""
      : "") +
    (floorIndex === bank.bank_floors.length - 1
      ? " monitoring__card__liftContainerBorderBottom  "
      : "") +
    (floorIndex === 0 ? "monitoring__card__liftContainerBorderTop" : "");

  return <div className={containerClass}>{getIcon()}</div>;
}

export default React.memo(IconDefinitionElevatorCustom);
